import type { Permission, Role, UserIdentity } from './types';

export interface AuthPayload {
  sub: string;
  email: string;
  orgId: number;
  orgName: string;
  role: Role;
  permissions?: Permission[];
  isSuperAdmin?: boolean;
  exp?: number;
}

const ACCESS_TOKEN_KEY = 'dominoris.token';
const REFRESH_TOKEN_KEY = 'dominoris.refreshToken';
const USER_ID_KEY = 'dominoris.userId';
const ORG_ID_KEY = 'dominoris.orgId';
const REMEMBER_KEY = 'dominoris.remember';
const AUTH_EVENT = 'dominoris.auth';
const TERMS_ACCEPTED_KEY = 'dominoris.termsAccepted';

// Permission templates are now server-side only (permissions.constants.ts).
// JWT always includes resolved permissions from the backend.

function getStoredValue(key: string): string | null {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

function setStoredValue(key: string, value: string, remember: boolean): void {
  if (remember) {
    localStorage.setItem(key, value);
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.setItem(key, value);
    localStorage.removeItem(key);
  }
}

function clearStoredValue(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export function isTokenExpired(token: string): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) return true;
  try {
    const payload = JSON.parse(atob(parts[1] ?? '')) as { exp?: number };
    if (!payload.exp) return false;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp < nowSeconds;
  } catch {
    return true;
  }
}

export function getToken(): string | null {
  const token = getStoredValue(ACCESS_TOKEN_KEY);
  if (!token) return null;
  if (isTokenExpired(token)) return null;
  return token;
}

export function getRefreshToken(): string | null {
  return getStoredValue(REFRESH_TOKEN_KEY);
}

export function getStoredUserId(): string | null {
  return getStoredValue(USER_ID_KEY);
}

export function getStoredOrgId(): number | null {
  const value = getStoredValue(ORG_ID_KEY);
  if (!value) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function setTokens(accessToken: string, refreshToken: string, remember = false): void {
  setStoredValue(ACCESS_TOKEN_KEY, accessToken, remember);
  setStoredValue(REFRESH_TOKEN_KEY, refreshToken, remember);
  const payload = parseToken(accessToken);
  if (payload?.sub) {
    setStoredValue(USER_ID_KEY, payload.sub, remember);
  }
  if (payload?.orgId) {
    setStoredValue(ORG_ID_KEY, String(payload.orgId), remember);
  }
  localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
  notifyAuthChanged();
}

export function clearTokens(): void {
  clearStoredValue(ACCESS_TOKEN_KEY);
  clearStoredValue(REFRESH_TOKEN_KEY);
  clearStoredValue(USER_ID_KEY);
  clearStoredValue(ORG_ID_KEY);
  clearStoredValue(TERMS_ACCEPTED_KEY);
  // passkeyPromptDismissed is scoped per userId — no need to clear on logout
  notifyAuthChanged();
}

export function getRememberPreference(): boolean {
  const stored = localStorage.getItem(REMEMBER_KEY);
  if (stored === '1') return true;
  if (stored === '0') return false;
  // Default to true — sessionStorage is destroyed on tab close
  return true;
}

export function getTermsAccepted(): boolean {
  return getStoredValue(TERMS_ACCEPTED_KEY) === '1';
}

export function setTermsAccepted(accepted: boolean): void {
  // Cycle 9 v7.35.0: terms acceptance e ato legal do USUARIO, nao da
  // sessao de login. Persistir SEMPRE em localStorage independente do
  // remember={true|false}. Antes sessionStorage perdia o aceite ao
  // fechar/reabrir browser ou em segunda aba pos-restart, forcando
  // duplo POST /auth/accept-terms (audit trail poluido).
  // remember=true mantido pra outros campos (token, etc).
  setStoredValue(TERMS_ACCEPTED_KEY, accepted ? '1' : '0', true);
  notifyAuthChanged();
}

export function onAuthChange(handler: () => void): () => void {
  window.addEventListener(AUTH_EVENT, handler);
  return () => window.removeEventListener(AUTH_EVENT, handler);
}

function notifyAuthChanged(): void {
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function parseToken(token: string): AuthPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1] ?? '')) as AuthPayload;
    return payload;
  } catch {
    return null;
  }
}

export function getCurrentUser(): UserIdentity | null {
  const token = getToken();
  if (!token) return null;
  const payload = parseToken(token);
  if (!payload) return null;
  const permissions = payload.permissions ?? [];
  return {
    id: payload.sub,
    email: payload.email,
    orgId: payload.orgId,
    orgName: payload.orgName,
    role: payload.role,
    permissions,
    isSuperAdmin: payload.isSuperAdmin ?? false
  };
}

export function hasPermission(permission: Permission): boolean {
  const user = getCurrentUser();
  if (!user) return false;
  // Cycle 8 v7.34.0: pii.view-original NÃO é bypassável por SuperAdmin
  // (CLAUDE.md raiz: PermissionsGuard tem exceção explícita server-side).
  // Frontend precisa espelhar pra não mostrar botão habilitado que API
  // rejeita com 403 — confunde SuperAdmin + auditoria poluída.
  if (permission === 'pii.view-original') {
    return user.permissions.includes(permission);
  }
  if (user.isSuperAdmin) return true;
  return user.permissions.includes(permission);
}

/** Returns the expiration ISO string from a stored access token, or null. */
export function getTokenExpiresAt(): string | null {
  const token = getStoredValue(ACCESS_TOKEN_KEY);
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1] ?? '')) as { exp?: number };
    if (!payload.exp) return null;
    return new Date(payload.exp * 1000).toISOString();
  } catch {
    return null;
  }
}
